import moment from "moment";
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { db } from "../lib/firebase";
import { investmentPlans } from "../lib/plans";

// Extract and format plans from global config to use in the app
export const plans = investmentPlans.map(plan => ({
  name: plan.name,
  amount: plan.amount,
  duration: plan.duration,
  profit: plan.profit,
  maxTps: plan.maxTps
}));

// Fetch user data including role, user info, and current active trading transaction
export const fetchUserData = async (user) => {
  if (!user?.uid) return null;
  const userRef = doc(db, "stock", user.uid);
  const userDoc = await getDoc(userRef);
  if (!userDoc.exists()) return null;

  const userData = userDoc.data();
  const role = userData.role;

  // Only include active trading transactions (limit to first one)
  const activeTransactions = (userData.transactions || [])
    .filter(t => t.account === "Trading" && t.stage?.toLowerCase() === "active")
    .slice(0, 1);

  return { role, activeTransactions, userData };
};

// Calculate profit breakdown based on plan and amount invested
export const calculateProfitDistribution = (amount, plan) => {
  const profitPercentage = plan.profit / 100;
  const totalProfit = Math.floor(amount * profitPercentage) * 6;



  const now = moment();

  // Equal daily payout amount
  const payoutAmount = Math.floor(totalProfit / plan.duration);

  // Build distributions array over plan.duration days
  const distributions = [];
  for (let i = 1; i <= plan.duration; i++) {
    // Last payout gets remainder to fix rounding
    const amountForThisDay = (i === plan.duration)
      ? totalProfit - payoutAmount * (plan.duration - 1)
      : payoutAmount;

    distributions.push({
      amount: amountForThisDay,
      date: now.clone().add(i, 'days').format("MMMM D, YYYY"),
      time: "09:00:00",
      scheduled: now.clone().add(i, 'days').toISOString(),
      percent: Math.round((amountForThisDay / totalProfit) * 100),
      stage: "pending"
    });
  }

  return {
    totalProfit,
    distributions,
    finalAmount: amount + totalProfit,
    lastPaymentDate: distributions[distributions.length - 1].scheduled
  };
};

// Handle trade start
export const handleStartTrade = async ({
  user,
  selectedPlan,
  investmentAmount,
  accountBalances,
  setSuccess,
  navigate,
  setAccountBalances
}) => {
  const plan = plans.find(p => p.name === selectedPlan);
  const amount = parseFloat(investmentAmount);
  const now = moment();
  const userRef = doc(db, "stock", user.uid);
  const profitDetails = calculateProfitDistribution(amount, plan);

  // Create investment record for deduction
  const investmentTransaction = {
    account: "Investments",
    amount,
    beneficiary: "Trading Bal",
    date: now.format("MMMM D, YYYY"),
    time: now.format("HH:mm:ss"),
    description: `Investment for ${plan.name}`,
    type: "debit",
    stage: "complete"
  };

  // Create trading record showing credit and profit setup
  const tradingTransaction = {
    account: "Trading",
    amount,
    beneficiary: user.email,
    date: now.format("MMMM D, YYYY"),
    time: now.format("HH:mm:ss"),
    description: `Profits from ${plan.name}`,
    type: "credit",
    stage: "active",
    planDetails: {
      name: plan.name,
      initialAmount: amount,
      duration: plan.duration,
      totalProfit: profitDetails.totalProfit
    },
    profitSchedule: profitDetails.distributions
  };

  // Update user document with balance changes and transaction history
  try {
    await updateDoc(userRef, {
      "accountBalances.Investments": (accountBalances.Investments || 0) - amount,
      "accountBalances.Trading": (accountBalances.Trading || 0) + amount + profitDetails.totalProfit,
      role: "investor",
      transactions: arrayUnion(investmentTransaction, tradingTransaction),
      activeInvestments: arrayUnion({
        plan: plan.name,
        amount,
        startDate: now.toISOString(),
        profitSchedule: profitDetails.distributions,
        stage: "active"
      })
    });

    // Update the local state to reflect the new balances after the transaction
    setAccountBalances({
      Investments: (accountBalances.Investments || 0) - amount,
      Trading: (accountBalances.Trading || 0) + amount + profitDetails.totalProfit
    });

    setSuccess(`Investment completed! Total profit: $${profitDetails.totalProfit}`);
  } catch (err) {
    // Do not set error message
    console.error("Error during trade start:", err);
  } finally {
    // This will execute regardless of success or failure
    setTimeout(() => {
      navigate("/");
      window.location.reload();
    }, 1000); // Redirect after 1 second
  }
};

// Handle claiming profit from today's eligible schedule
export const handleClaimWithdrawal = async ({
  user,
  transaction,
  accountBalances,
  setSuccess,
  navigate
}) => {
  const now = moment();
  const today = now.format("MMMM D, YYYY");

  // Find the schedule for today's date
  const scheduleToClaim = transaction.profitSchedule?.find(s =>
    moment(s.date).format("MMMM D, YYYY") === today
  );
  if (!scheduleToClaim) return; // Do nothing if no profit to claim

  const userRef = doc(db, "stock", user.uid);
  const userDoc = await getDoc(userRef);
  const userData = userDoc.data();

  // Record the claim as a new transaction
  const claimTransaction = {
    account: "Trading",
    amount: scheduleToClaim.amount,
    beneficiary: "Trading",
    date: now.format("MMMM D, YYYY"),
    time: now.format("HH:mm:ss"),
    description: `Profit claim from ${transaction.planDetails?.name}`,
    type: "credit",
    stage: "pending",
    relatedTrade: transaction.planDetails?.name
  };

  // Remove the claimed schedule from profitSchedule
  const updatedProfitSchedule = transaction.profitSchedule.filter(
    s => s.scheduled !== scheduleToClaim.scheduled
  );

  const isLastPayment = updatedProfitSchedule.length === 0;

  // Filter out any previously claimed pending transactions from trading
  let updatedTransactions = (userData.transactions || [])
    .filter(t => !(t.account === "Trading" && t.stage === "active" && t.description === transaction.description));

  updatedTransactions.push({
    ...transaction,
    profitSchedule: updatedProfitSchedule
  });

  updatedTransactions.push(claimTransaction);

  // Base update data
  const updateData = {
    "accountBalances.Investments": (accountBalances.Investments || 0) + scheduleToClaim.amount,
    "accountBalances.Trading": (accountBalances.Trading || 0) - scheduleToClaim.amount,
    transactions: updatedTransactions
  };

  // If it's the last payment, finalize cleanup and profit summary
  if (isLastPayment) {
    updateData.role = "user";

    // Final investment credit for total profit
    const investmentProfitTransaction = {
      account: "Investments",
      amount: transaction.planDetails.totalProfit,
      beneficiary: user.email,
      date: now.format("MMMM D, YYYY"),
      time: now.format("HH:mm:ss"),
      description: `Total profit from ${transaction.planDetails?.name} plan`,
      type: "credit",
      stage: "complete"
    };

    // Remove all transactions with account: "Trading" or description matching original investment
    updatedTransactions = updatedTransactions.filter(t =>
      !(t.account === "Trading") &&
      !(t.account === "Investments" && t.description === `Investment in ${transaction.planDetails?.name}`)
    );

    // Remove the active investment record
    updateData.activeInvestments = arrayRemove({
      plan: transaction.planDetails?.name,
      amount: transaction.amount,
      startDate: transaction.date,
      profitSchedule: transaction.profitSchedule,
      status: "active"
    });

    // Add the final profit credit transaction
    updatedTransactions.push(investmentProfitTransaction);

    // Update with cleaned transactions
    updateData.transactions = updatedTransactions;
  }

  // Commit updates to Firestore
  await updateDoc(userRef, updateData);

  // Redirect after delay
  setTimeout(() => {
    navigate("/");
    window.location.reload();
  }, 1000); // 1 second delay before redirect
};
